import {
  GAIT_LIST,
  GAIT_TASKS,
  PRESETS,
  TRIM_SPECS,
  isMove,
  presetFor,
  type GaitTask,
} from '@/domain/hs1'
import { Alert, Badge, Button, Card, Switch } from '@/components/ui'
import { useSimulation } from '@/sim/SimulationProvider'
import { Metric, Slider, num } from '@/sim/parts'

/**
 * Bewegung — was ein Mensch verstellt.
 *
 * ZWEI BETRIEBSARTEN, UND DER UNTERSCHIED IST DIE HALBE SEITE:
 *
 *   Aufgezeichnet — die Bewegung kommt aus den Normkurven. Die Regler verformen
 *   sie, die Gangbilder wandeln sie ab. Die Geschwindigkeit ist ein ERGEBNIS.
 *
 *   Gerechnet — die Bewegung entsteht aus einer Aufgabe: Schrittlänge,
 *   Standphasenanteil, Fussfreigang. Die Geschwindigkeit ist eine VORGABE, und
 *   die Kinematik meldet, wenn die Maschine nicht mitkommt.
 */

/**
 * Was die Maschine tun kann.
 *
 * DIE TRENNSTRICHE SIND DIE AUSSAGE. Die ersten vier sind GÄNGE: sie haben eine
 * Schrittlänge, einen Standphasenanteil und zwei Beine, die eine halbe Periode
 * auseinanderlaufen. Nur für sie gibt es einen gerechneten Betrieb, weil nur
 * sie eine Gangaufgabe haben.
 *
 * Stehen ist eine Haltung. Hackensprung und Aufheben sind Skripte mit
 * gleichphasigen Beinen — beim Sprung springen beide zugleich, beim Aufheben
 * kniet eines ab. Für sie gibt es weder Schrittlänge noch Auswahl an
 * Gangbildern; sie sind, was sie sind.
 */
/* Die Liste steht im Rechenkern — sie gehört zu `GaitKind` und stand hier
   ein zweites Mal. */
const GAITS = GAIT_LIST
const GAIT_GROUPS = ['Gänge', 'Haltung und Skripte'] as const

/** Grenzen der Gangaufgabe — sie gehören in den Kern, nicht an den Schieberegler. */
const TASK_SPECS: {
  key: keyof GaitTask
  label: string
  min: number
  max: number
  step: number
  unit: string
  note: string
}[] = [
  {
    key: 'step',
    label: 'Schrittlänge',
    min: 0.2,
    max: 1.2,
    step: 0.01,
    unit: 'm',
    note: 'Halbe Doppelschrittlänge',
  },
  {
    key: 'duty',
    label: 'Standphasenanteil',
    min: 0.2,
    max: 0.9,
    step: 0.01,
    unit: '',
    note: 'Unter 0,5 entsteht eine Flugphase',
  },
  {
    key: 'clear',
    label: 'Fussfreigang',
    min: 0.02,
    max: 0.25,
    step: 0.005,
    unit: 'm',
    note: 'Höhe des Schwungfusses',
  },
  {
    key: 'flex',
    label: 'Beinstreckung',
    min: 0.9,
    max: 1,
    step: 0.002,
    unit: '',
    note: 'Anteil der gestreckten Beinlänge in der Standmitte',
  },
  {
    key: 'bob',
    label: 'Beckenabsenkung',
    min: 0,
    max: 0.15,
    step: 0.002,
    unit: 'm',
    note: 'Nur ohne Flugphase wirksam',
  },
  {
    key: 'touch',
    label: 'Aufsetzwinkel',
    min: -15,
    max: 30,
    step: 0.5,
    unit: '°',
    note: 'Zehen hoch beim Fersenaufsatz',
  },
  {
    key: 'push',
    label: 'Abstosswinkel',
    min: -45,
    max: 0,
    step: 0.5,
    unit: '°',
    note: 'Zehen runter beim Abdruck',
  },
  {
    key: 'sway',
    label: 'Seitliche Verlagerung',
    min: 0,
    max: 0.1,
    step: 0.002,
    unit: 'm',
    note: 'Zum Standbein hin',
  },
  {
    key: 'track',
    label: 'Halbe Spurbreite',
    min: 0.04,
    max: 0.2,
    step: 0.005,
    unit: 'm',
    note: 'Abstand des Fusses zur Mittellinie',
  },
  {
    key: 'lean',
    label: 'Rumpfneigung',
    min: -8,
    max: 14,
    step: 0.5,
    unit: '°',
    note: 'Nach vorn positiv',
  },
  {
    key: 'yaw',
    label: 'Beckendrehung',
    min: 0,
    max: 12,
    step: 0.5,
    unit: '°',
    note: 'Amplitude um die Hochachse',
  },
  {
    key: 'arm',
    label: 'Armschwung',
    min: 0,
    max: 1.4,
    step: 0.02,
    unit: '',
    note: 'Anteil des Hüftwinkels',
  },
]

export function Motion() {
  const { state, simulation, setState, setTrim, setTask, setGait, setPreset, reset } =
    useSimulation()
  const { metrics, gait } = simulation
  const preset = presetFor(state.gait, state.preset)
  /* Gerechnet werden können nur GÄNGE. Stehen ist eine Haltung, Hackensprung
     und Aufheben sind Skripte — keines von ihnen hat eine Gangaufgabe. */
  const scripted = state.gait === 'stand' || isMove(state.gait)
  const computed = state.generated && !scripted

  return (
    <div className="stack">
      <div className="metrics">
        <Metric
          label="Geschwindigkeit"
          value={`${num(metrics.speed, 2)} m/s`}
          note={computed ? 'aus der Aufgabe' : 'aus den Kurven gemessen'}
        />
        <Metric label="Doppelschritt" value={`${num(metrics.stride, 2)} m`} />
        <Metric
          label="Zyklusdauer"
          value={`${num(metrics.cycleTime, 3)} s`}
          note={`${metrics.cadence} Schritte/min`}
        />
        <Metric
          label="Standphase"
          value={`${num(100 * metrics.stance, 0)} %`}
          note={metrics.flight > 0 ? `Flug ${num(1000 * metrics.flight, 0)} ms` : 'ohne Flugphase'}
        />
        <Metric
          label="Kinematik"
          value={metrics.clipped === 0 ? 'frei' : `${metrics.clipped}× Anschlag`}
          warn={metrics.clipped > 0}
          note={
            metrics.clipped > 0
              ? `verlangte Beinlänge ${num(100 * gait.report.reach, 1)} %`
              : 'kein Anschlag'
          }
        />
      </div>

      <div className="workbench">
        <div className="workbench__aside stack">
          <Card title="Bewegung">
            <div className="stack stack--tight">
              {GAIT_GROUPS.map((group) => (
                <div key={group} className="stack stack--tight">
                  <span className="eyebrow">{group}</span>
                  {GAITS.filter((e) => e.group === group).map((entry) => (
                    <Button
                      key={entry.key}
                      variant={state.gait === entry.key ? 'primary' : 'secondary'}
                      onClick={() => setGait(entry.key)}
                    >
                      {entry.label}
                    </Button>
                  ))}
                </div>
              ))}
            </div>
            <p className="metric__note" style={{ marginTop: 'var(--space-3)' }}>
              {GAITS.find((e) => e.key === state.gait)?.note}
            </p>
            <div style={{ marginTop: 'var(--space-3)' }}>
              <Button size="sm" variant="ghost" block onClick={reset}>
                Regler und Aufgabe zurücksetzen
              </Button>
            </div>
          </Card>

          <Card title="Betriebsart">
            <Switch
              label="Gerechneter Gang"
              checked={state.generated}
              disabled={scripted}
              onChange={(event) => setState({ generated: event.target.checked })}
            />
            <p className="metric__note" style={{ marginTop: 'var(--space-2)' }}>
              {computed
                ? 'Die Bewegung entsteht aus der Aufgabe unten.'
                : 'Die Bewegung kommt aus den aufgezeichneten Normkurven.'}
            </p>
            {scripted && (
              <p className="metric__note" style={{ marginTop: 'var(--space-3)' }}>
                {state.gait === 'stand'
                  ? 'Stehen kennt keine Schrittlänge — es gibt dort nichts zu rechnen.'
                  : 'Eine Skriptbewegung hat keine Gangaufgabe: keine Schrittlänge, keinen wählbaren Standphasenanteil. Was sie tut, steht in ihren Stützstellen.'}
              </p>
            )}
          </Card>

          {state.gait === 'carry' && (
            <Card title="Zuladung">
              <Slider
                label="Masse"
                value={state.load}
                min={0}
                max={80}
                step={1}
                unit="kg"
                onChange={(load) => setState({ load })}
              />
              <p className="metric__note">
                Beidhändig vor dem Körper. Wo sie sitzt, steht im Modell.
              </p>
            </Card>
          )}
        </div>

        <div className="stack">
          <Card
            title="Gangbild"
            eyebrow={computed ? 'wirkt auf die Regler' : 'formt die Kurven'}
            actions={<Badge tone="neutral">{PRESETS[state.gait].length}</Badge>}
          >
            <div className="stack stack--tight" style={{ marginBottom: 'var(--space-4)' }}>
              {PRESETS[state.gait].map((entry) => (
                <Button
                  key={entry.key}
                  variant={state.preset === entry.key ? 'primary' : 'ghost'}
                  onClick={() => setPreset(entry.key)}
                >
                  {entry.label}
                </Button>
              ))}
            </div>
            <p className="page-lead">{preset.note}</p>
            {computed && (
              <Alert tone="info" title="Im gerechneten Betrieb wirkt nur die Reglervorbelegung">
                Die Kurveneingriffe eines Gangbilds (<code>mod</code>, <code>warp</code>) greifen an
                den aufgezeichneten Linien an. Der gerechnete Gang hat keine — er baut die Bewegung
                aus der Aufgabe neu auf.
              </Alert>
            )}
          </Card>

          {/*
            EINE SKRIPTBEWEGUNG KENNT NUR DIE KADENZ. Die übrigen Regler
            verformen aufgezeichnete Normkurven um ihren Mittelwert; ein Skript
            hat keine, und sie liefen ins Leere. Sie trotzdem zu zeigen wäre ein
            Bedienelement, das nichts tut.

            Die Kadenz bleibt, weil sie hier etwas anderes bedeutet — nicht
            Schritte je Minute, sondern die Dauer eines Durchgangs.
          */}
          <Card
            title="Regler"
            eyebrow={
              scripted
                ? state.gait === 'stand'
                  ? 'im Stand wirkt keiner'
                  : 'ein Skript kennt nur die Zyklusdauer'
                : 'Referenzgang'
            }
          >
            <div className="stack stack--tight">
              {(scripted
                ? TRIM_SPECS.filter((spec) => spec.key === 'cadence' && state.gait !== 'stand')
                : TRIM_SPECS
              ).map((spec) => (
                <Slider
                  key={spec.key}
                  label={spec.label}
                  value={state.trim[spec.key]}
                  min={spec.min}
                  max={spec.max}
                  step={spec.step}
                  unit={spec.unit}
                  onChange={(value) => setTrim({ [spec.key]: value })}
                />
              ))}
            </div>
            <p className="metric__note" style={{ marginTop: 'var(--space-3)' }}>
              {scripted ? (
                state.gait === 'stand' ? (
                  <>
                    Im Stand gibt es keine Kurve zu verformen und keinen Zyklus zu takten. Die
                    Haltung steht als fester Winkelsatz im Kern.
                  </>
                ) : (
                  <>
                    Die Kadenz ist hier keine Schrittzahl, sondern die <strong>Dauer</strong> eines
                    Durchgangs: bei {state.trim.cadence} entspricht das {num(metrics.cycleTime, 2)}{' '}
                    s.{' '}
                    {state.gait === 'hop'
                      ? 'Daraus folgt die Flughöhe — ein kürzerer Zyklus heisst weniger Zeit in der Luft und damit höhere Momente.'
                      : 'Ein kürzerer Durchgang heisst dieselbe Bewegung in weniger Zeit — und damit höhere Beschleunigungen und Momente.'}
                  </>
                )
              ) : (
                <>
                  Die Regler verformen die Kurven um ihren <strong>Mittelwert</strong>, nicht um
                  null — sonst verschiebt sich mit der Amplitude auch die Haltung. Die Kadenz wirkt
                  in beiden Betriebsarten.
                </>
              )}
            </p>
          </Card>

          {computed && (
            <Card title="Gangaufgabe">
              <div className="stack stack--tight">
                {TASK_SPECS.map((spec) => (
                  <Slider
                    key={spec.key}
                    label={spec.label}
                    value={state.task[spec.key]}
                    min={spec.min}
                    max={spec.max}
                    step={spec.step}
                    unit={spec.unit}
                    onChange={(value) => setTask({ [spec.key]: value })}
                  />
                ))}
              </div>
              <div style={{ marginTop: 'var(--space-4)' }}>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() =>
                    setTask(GAIT_TASKS[(state.gait === 'stand' ? 'walk' : state.gait) as 'walk'])
                  }
                >
                  Aufgabe zurücksetzen
                </Button>
              </div>
              <p className="metric__note" style={{ marginTop: 'var(--space-3)' }}>
                Nur diese Zahlen unterscheiden die Gangarten im gerechneten Betrieb — die Kette
                dahinter ist jedes Mal dieselbe. Ein Standphasenanteil unter 0,5 erzeugt eine
                Flugphase, und die Beckenbahn wird dort zur Wurfparabel.
              </p>
            </Card>
          )}

          {gait.report.warn && (
            <Alert tone="warning" title="Die Aufgabe passt nicht zur Maschine">
              Die Kinematik klemmt {metrics.clipped}-mal je Zyklus; verlangt werden{' '}
              {num(100 * gait.report.reach, 1)} % der gestreckten Beinlänge — die Beinlänge reicht
              nicht. Abhilfe schaffen eine geringere Beinstreckung oder ein kürzerer Schritt.
            </Alert>
          )}
        </div>
      </div>
    </div>
  )
}
